import { useTranslation } from 'react-i18next';
import { useContacts } from './useContacts';
import { YandexMap } from './YandexMap';
import { Spinner } from '@/components/Spinner';
import { openExternal } from '@/lib/tg';

export function ContactsView() {
  const { t } = useTranslation();
  const { data, isLoading } = useContacts();

  if (isLoading)
    return (
      <div className="py-8 text-center text-hint">
        <Spinner />
      </div>
    );
  if (!data) return <p className="text-hint">{t('common.error')}</p>;

  return (
    <div className="flex flex-col gap-4">
      <YandexMap lat={data.mapLat} lng={data.mapLng} />

      <Section title={t('contacts.address')}>
        <p>{data.address}</p>
      </Section>

      <Section title={t('contacts.workingHours')}>
        <p>Пн–Чт: {data.workingHours.monThu}</p>
        <p>Пт–Сб: {data.workingHours.friSat}</p>
        {data.workingHours.sun ? <p>Вс: {data.workingHours.sun}</p> : null}
        {data.workingHours.note ? (
          <p className="text-hint text-xs">{data.workingHours.note}</p>
        ) : null}
      </Section>

      <Section title={t('contacts.phones')}>
        <ul className="flex flex-col gap-2">
          {data.phones.map((p) => (
            <li key={p.number}>
              <a
                href={`tel:${p.number}`}
                className="font-mono tracking-[0.06em] text-white hover:text-white/80 transition-colors"
              >
                {p.label}: {p.number}
              </a>
            </li>
          ))}
        </ul>
      </Section>

      {data.parkingInfo ? (
        <Section title={t('contacts.parking')}>
          <p>{data.parkingInfo}</p>
        </Section>
      ) : null}

      {data.wifi ? (
        <Section title={t('contacts.wifi')}>
          <p>SSID: {data.wifi.ssid}</p>
          <p>Пароль: {data.wifi.password}</p>
        </Section>
      ) : null}

      {data.socials.length > 0 ? (
        <Section title={t('contacts.socials')}>
          <ul className="flex flex-wrap gap-2">
            {data.socials.map((s) => (
              <li key={s.url}>
                <button
                  type="button"
                  onClick={() => openExternal(s.url)}
                  className="rounded-full px-3.5 py-1.5 text-[11px] font-mono uppercase tracking-[0.14em] transition-colors"
                  style={{
                    background: 'rgba(255,255,255,0.035)',
                    border: '1px solid var(--line-2)',
                    color: 'var(--text-1)',
                  }}
                >
                  {s.label}
                </button>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section
      className="relative rounded-2xl p-4"
      style={{
        background: 'rgba(255,255,255,0.02)',
        border: '1px solid var(--line-1)',
        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
      }}
    >
      <h2 className="eyebrow mb-2">{title}</h2>
      <div className="text-sm flex flex-col gap-1">{children}</div>
    </section>
  );
}
